import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import { fetchServices } from "../store/slice/service/serviceThunks";
import { fetchStates } from "../store/slice/location/locationThunks";
import { apiFetch } from "../utils/api";

interface PriceEstimate {
  serviceId: number;
  serviceName: string;
  sampleSize: number;
  averagePrice: number | null;
  minPrice: number | null;
  maxPrice: number | null;
}

const JobEstimateCalculator: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { services } = useSelector((state: RootState) => state.service);
  const { states } = useSelector((state: RootState) => state.location);

  const [serviceId, setServiceId] = useState<number | "">("");
  const [stateId, setStateId] = useState<number | "">("");
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [error, setError] = useState("");
  const [estimate, setEstimate] = useState<PriceEstimate | null>(null);

  useEffect(() => {
    dispatch(fetchServices());
    dispatch(fetchStates());
  }, [dispatch]);

  const handleGetEstimate = async () => {
    if (!serviceId) {
      setError("Please select a service first.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError("");
    setEstimate(null);
    try {
      const params = new URLSearchParams({ serviceId: String(serviceId) });
      if (stateId) params.append("stateId", String(stateId));
      const data = await apiFetch(`/api/providers/price-estimate?${params.toString()}`);
      setEstimate(data);
      setStatus("idle");
    } catch (e: any) {
      setError(e.message || "Couldn't get an estimate right now.");
      setStatus("error");
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
      <h3 className="text-xl font-bold text-gray-900 mb-1">
        Get a Job Estimate
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        See typical pricing from verified providers on our platform.
      </p>

      <select
        className="w-full mb-3 p-2 border rounded"
        value={serviceId}
        onChange={(e) => setServiceId(e.target.value ? Number(e.target.value) : "")}
      >
        <option value="">Select a service</option>
        {services.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>

      <select
        className="w-full mb-4 p-2 border rounded"
        value={stateId}
        onChange={(e) => setStateId(e.target.value ? Number(e.target.value) : "")}
      >
        <option value="">All states (optional)</option>
        {states.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>

      <button
        onClick={handleGetEstimate}
        disabled={status === "loading"}
        className="w-full bg-green-700 hover:bg-green-800 text-white font-semibold py-2 px-4 rounded disabled:opacity-50"
      >
        {status === "loading" ? "Calculating..." : "Get Estimate"}
      </button>

      {status === "error" && (
        <p className="text-red-500 text-sm mt-3">{error}</p>
      )}

      {estimate && (
        <div className="mt-5 pt-4 border-t border-gray-200">
          {estimate.sampleSize > 0 ? (
            <>
              <p className="text-sm text-gray-500 mb-1">
                Typical price for {estimate.serviceName}
              </p>
              <p className="text-2xl font-bold text-gray-900">
                ₦{estimate.averagePrice?.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Range: ₦{estimate.minPrice?.toLocaleString()} – ₦
                {estimate.maxPrice?.toLocaleString()}, based on{" "}
                {estimate.sampleSize} verified provider
                {estimate.sampleSize === 1 ? "" : "s"}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              Not enough verified providers for this service yet to show a
              reliable estimate. Try browsing available providers instead.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default JobEstimateCalculator;
